import React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "@apollo/client";
import Cookies from "js-cookie";
import jwt_decode from "jwt-decode";
import { ENTITY_TYPE, ESTABLISHMENT_STATUS, USER_ROLE } from "../common/model/enum";
import { GET_ADMIN_ESTABLISHMENTS, buildAdminEstablishmentsVariables } from "./AdminAPI";

export const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdmin must be used within an AdminProvider");
  }
  return context;
};

export const getAuthTokens = () => {
  const encryptedToken = Cookies.get("ZORNI_AUTH_TOKEN");
  return {
    token: encryptedToken,
  };
};

// États de chargement de la liste centrale des établissements.
export const ESTABLISHMENTS_STATUS = {
  LOADING: "loading",
  ERROR: "error",
  READY: "ready",
};

const EMPTY_LIST = [];

// Lecture pure du JWT : sert à initialiser l'état de façon synchrone (la
// liste des établissements peut ainsi partir dès le premier rendu) et à le
// resynchroniser quand le token est renouvelé.
const decodeAdminToken = (token) => {
  const result = {
    isOwner: false,
    email: "",
    hasApplicationRoles: false,
    organization: null,
    establishmentRoles: [],
    hasPARole: false,
  };
  if (!token) return result;

  try {
    const decoded = jwt_decode(token);
    if (!decoded) return result;

    result.isOwner = decoded.owner;
    result.email = decoded.email;

    if (decoded.applicationRoles) {
      const applicationRolesScope = decoded.applicationRoles;
      result.hasApplicationRoles = true;

      const organizationRole = applicationRolesScope.find(
        (role) => role.entityType === "ORGANIZATION"
      );
      if (organizationRole) {
        result.organization = organizationRole;
      } else if (decoded.parentOrganism) {
        result.organization = {
          entityId: decoded.parentOrganism,
          entityType: "ORGANIZATION",
        };
      }

      result.establishmentRoles = applicationRolesScope.filter(
        (role) => role.entityType === "ESTABLISHMENT"
      );
      result.hasPARole = result.establishmentRoles.some(
        (item) => item.role === USER_ROLE.PA
      );
    }
  } catch (error) {
    console.error("Error decoding token:", error);
  }
  return result;
};

export const AdminProvider = ({ children }) => {
  const { token } = getAuthTokens();
  // Décodé une seule fois pour l'état initial (voir decodeAdminToken).
  const initialRef = useRef(null);
  if (initialRef.current === null) initialRef.current = decodeAdminToken(token);
  const initial = initialRef.current;

  const [organization, setOrganization] = useState(initial.organization);
  const [establishmentsPermission, setEstablishmentsPermission] = useState(
    initial.establishmentRoles
  );
  const [hasPARole, setHasePARole] = useState(initial.hasPARole);
  const [isAuthUserOwner, setIsAuthUserOwner] = useState(initial.isOwner);
  const [authUserEmail, setAuthUserEmail] = useState(initial.email);

  // Renouvellement du token : l'état initial vient déjà du même token, on ne
  // resynchronise donc que lorsqu'il change réellement.
  const syncedTokenRef = useRef(token);
  useEffect(() => {
    if (syncedTokenRef.current === token) return;
    syncedTokenRef.current = token;
    if (!token) return;

    const decoded = decodeAdminToken(token);
    setIsAuthUserOwner(decoded.isOwner);
    setAuthUserEmail(decoded.email);
    if (decoded.hasApplicationRoles) {
      if (decoded.organization) setOrganization(decoded.organization);
      setEstablishmentsPermission(decoded.establishmentRoles);
      setHasePARole(decoded.hasPARole);
    }
  }, [token]);

  const addOrganization = (entityId) => {
    if (!entityId) return;
    const newOrganization = {
      entityId: entityId,
      role: USER_ROLE.PSA,
      entityType: "ORGANIZATION",
    };
    setOrganization(newOrganization);
  };

  const triggerEstablishmentsPermissions = (entityId) => {
    setEstablishmentsPermission((prev) => [
      ...prev,
      {
        entityId: entityId,
        entityType: ENTITY_TYPE.ESTABLISHMENT,
        role: USER_ROLE.PA,
      },
    ]);
  };

  const isProAdminForEstablishment = useCallback(
    (establishmentId) => {
      if (!establishmentId) return false;

      return (establishmentsPermission || []).some(
        (permission) =>
          permission?.role === USER_ROLE.PA &&
          String(permission?.entityId) === String(establishmentId)
      );
    },
    [establishmentsPermission]
  );

  const hasPaRole = hasPARole;

  // ── Liste centrale des établissements ────────────────────────────────────
  // Source unique pour toutes les pages de /admin (garde d'accès, tables,
  // filtres, sélecteurs) : un seul useQuery, monté avec le provider, donc une
  // seule requête à l'arrivée sur /admin. Les pages la lisent ici au lieu de
  // la redemander. Les mutations (création / suppression) la relisent via
  // refetchQueries (voir useEstablishment).
  const organizationId = organization?.entityId;
  const listQuery = useQuery(GET_ADMIN_ESTABLISHMENTS, {
    variables: buildAdminEstablishmentsVariables(organizationId),
    skip: !organizationId,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const listPayload = listQuery.data?.QR_LIST_ESTABLISHMENT;
  const listContent = listPayload?.data?.content;
  const establishments = useMemo(() => listContent || EMPTY_LIST, [listContent]);

  // Le controller peut répondre 200 avec l'erreur dans le corps (champ `error`),
  // en plus des erreurs GraphQL / réseau classiques.
  const listQueryError = listQuery.error;
  const listPayloadError = listPayload?.error;
  const establishmentsError = useMemo(
    () =>
      listQueryError ||
      (listPayloadError
        ? new Error(listPayloadError.message || "Establishment list error")
        : null),
    [listQueryError, listPayloadError]
  );

  let establishmentsStatus = ESTABLISHMENTS_STATUS.READY;
  if (organizationId && !listPayload?.data) {
    if (listQuery.loading) establishmentsStatus = ESTABLISHMENTS_STATUS.LOADING;
    else if (establishmentsError) establishmentsStatus = ESTABLISHMENTS_STATUS.ERROR;
  }

  const { refetch } = listQuery;
  const refetchEstablishments = useCallback(
    () => (organizationId ? refetch() : Promise.resolve()),
    [organizationId, refetch]
  );

  // Établissements « valides » : ACTIVATED uniquement.
  const validEstablishments = useMemo(
    () =>
      establishments.filter(
        (establishment) => establishment.status === ESTABLISHMENT_STATUS.ACTIVATED
      ),
    [establishments]
  );
  const hasValidEstablishment = validEstablishments.length > 0;

  // Établissements que l'utilisateur peut administrer : tous pour un PSA, les
  // établissements en rôle PA sinon. `userRole` (fourni par le serveur) prime ;
  // à défaut on retombe sur les rôles du JWT.
  const manageableEstablishments = useMemo(() => {
    if (organization?.role === USER_ROLE.PSA) return establishments;
    const paIds = new Set(
      (establishmentsPermission || [])
        .filter((permission) => permission.role === USER_ROLE.PA)
        .map((permission) => permission.entityId)
    );
    return establishments.filter((establishment) =>
      establishment.userRole
        ? establishment.userRole === USER_ROLE.PA
        : paIds.has(establishment.id)
    );
  }, [establishments, organization?.role, establishmentsPermission]);

  // Établissements auxquels on peut rattacher un utilisateur ou un circuit :
  // valides (ACTIVATED) ET administrables par l'utilisateur connecté. Source
  // unique des filtres « par établissement » (utilisateurs et circuits).
  const assignableEstablishments = useMemo(() => {
    const manageableIds = new Set(manageableEstablishments.map((item) => item.id));
    return validEstablishments.filter((item) => manageableIds.has(item.id));
  }, [validEstablishments, manageableEstablishments]);

  return (
    <AdminContext.Provider
      value={{
        organization,
        addOrganization,
        establishmentsPermission,
        hasPARole,
        hasPaRole,
        triggerEstablishmentsPermissions,
        isAuthUserOwner,
        isProAdminForEstablishment,
        authUserEmail,
        accessToken: token,
        establishments,
        validEstablishments,
        hasValidEstablishment,
        manageableEstablishments,
        assignableEstablishments,
        establishmentsStatus,
        establishmentsError,
        refetchEstablishments,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};
